import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Story } from '@/hooks/useStories';
import { cn } from '@/lib/utils';
import { BookOpen, PlusCircle } from 'lucide-react';
import { useState } from 'react';

interface StoriesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    stories: Story[];
    currentStoryId?: number | string;
    onSwitchStory: (story: Story) => void;
    onAddStory: () => void;
}

export function StoriesDialog({
    open,
    onOpenChange,
    stories,
    currentStoryId,
    onSwitchStory,
    onAddStory,
}: StoriesDialogProps) {
    const [searchQuery, setSearchQuery] = useState('');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden bg-card border-border shadow-2xl backdrop-blur-3xl sm:rounded-2xl duration-200">
                <DialogHeader className="px-6 py-4 space-y-1 bg-transparent">
                    <DialogTitle className="text-base font-medium tracking-wide text-foreground/90 uppercase flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            All Stories
                            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-[10px] h-5 px-1.5 font-normal">
                                {stories.length}
                            </Badge>
                        </span>
                    </DialogTitle>
                    <DialogDescription className="text-xs text-muted-foreground hidden">
                        Search and switch stories
                    </DialogDescription>
                </DialogHeader>

                <Command className="bg-transparent rounded-none">
                    <div className="flex items-center px-4 pb-2">
                        <CommandInput
                            placeholder="Search stories..."
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                            className="flex-1 bg-muted/30 border-none rounded-md focus:ring-0 h-9 text-xs placeholder:text-muted-foreground/50 px-3"
                        />
                    </div>

                    <CommandList className="max-h-[350px] overflow-y-auto custom-scrollbar p-2 border-t border-border/40">
                        <CommandEmpty className="py-12 text-center text-xs text-muted-foreground">
                            No stories found.
                        </CommandEmpty>

                        <CommandGroup className="px-1 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                            {stories.map((story) => {
                                const isCurrent = currentStoryId !== undefined && String(currentStoryId) === String(story.id);
                                return (
                                    <CommandItem
                                        key={story.id}
                                        value={story.name}
                                        onSelect={() => {
                                            onSwitchStory(story);
                                            onOpenChange(false);
                                        }}
                                        className={cn(
                                            "flex items-center justify-between p-2.5 rounded-lg border border-transparent transition-all mb-1 cursor-pointer group aria-selected:bg-muted/5",
                                            isCurrent ? "bg-primary/5 border-primary/10 aria-selected:bg-primary/5" : "hover:bg-muted/5 hover:border-transparent"
                                        )}
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className={cn(
                                                "h-8 w-8 rounded-md flex items-center justify-center shrink-0 transition-colors ring-1 ring-inset",
                                                isCurrent ? "bg-primary/10 text-primary ring-primary/20" : "bg-muted/10 text-muted-foreground ring-border/50 group-hover:bg-primary/5 group-hover:text-primary group-hover:ring-primary/10"
                                            )}>
                                                <BookOpen className="h-4 w-4" />
                                            </div>

                                            <div className="flex flex-col truncate gap-0.5">
                                                <span className={cn(
                                                    "text-sm font-medium truncate transition-colors",
                                                    isCurrent ? "text-primary" : "text-foreground group-hover:text-foreground"
                                                )}>
                                                    {story.name}
                                                </span>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground truncate">
                                                    {story.status && (
                                                        <Badge variant="outline" className={cn(
                                                            "capitalize text-[10px] h-3.5 px-1 rounded-[4px] border-border/50 font-normal leading-none",
                                                            story.status === 'completed' ? "text-green-500 bg-green-500/5 border-green-500/20" : "text-muted-foreground"
                                                        )}>
                                                            {story.status.replace('_', ' ')}
                                                        </Badge>
                                                    )}
                                                    {story.updated_at && <span className="text-[9px] opacity-60 uppercase tracking-wide">Updated {new Date(story.updated_at).toLocaleDateString()}</span>}
                                                </div>
                                            </div>
                                        </div>

                                        {isCurrent && (
                                            <div className="flex items-center">
                                                <Badge variant="secondary" className="bg-background text-primary shadow-sm border border-primary/20 text-[9px] h-5 px-1.5 gap-1 pointer-events-none uppercase tracking-wider font-medium">
                                                    Active
                                                </Badge>
                                            </div>
                                        )}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>

                <div className="p-3 bg-muted/5 flex justify-end items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenChange(false)}
                        className="h-7 text-xs text-muted-foreground hover:text-foreground rounded-md px-3"
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => {
                            onOpenChange(false);
                            onAddStory();
                        }}
                        className="h-7 gap-1.5 shadow-sm text-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3"
                    >
                        <PlusCircle className="h-3 w-3" />
                        Create Story
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
